// src/pages/CharacterSwitch.tsx（完全版：キャラクター切替後に「現在選択中」へスクロール）
//
// 追加点：
// - const currentSectionRef = useRef<HTMLElement | null>(null)
// - onSelectCharacter 成功時に currentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })

import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, type UserProfileItem, type UserProfileUpdateItem } from "../lib/api";
import { resolveBackendAssetUrl } from "../lib/backendAsset";
import { logError, toUserMessage } from "../lib/errors";
import { toProfileSaveErrorMessage } from "../lib/profileSaveError";
import {
  COMMUNICATION_STYLE_OPTIONS,
  PLAYFUL_COMMUNICATION_STYLE_OPTIONS,
  isPlayfulCommunicationStyle,
  resolveCommunicationStyle,
} from "../lib/profileFlow";
import { storage } from "../lib/storage";
import { getDeviceParams } from "../lib/device";

type CharacterVM = {
  id: string;
  name: string;
  description: string;
  initial: string;
  iconUrl?: string; // キャラクターアイコン画像URL
};

type DebugInfo = {
  time: string;
  phase: string;
  auth: ReturnType<typeof storage.getAuth>;
  device: ReturnType<typeof getDeviceParams>;
  note?: string;
  resCharacter?: any;
  normalized?: any;
};

const COMMUNICATION_STYLE_KEY = "communication_style";

function getByPath(obj: any, path: string): any {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

function pickStringByPaths(obj: any, paths: string[]): string | undefined {
  for (const path of paths) {
    const v = getByPath(obj, path);
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
  }
  return undefined;
}

function pickIdByPaths(obj: any, paths: string[]): string | undefined {
  for (const path of paths) {
    const v = getByPath(obj, path);
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function extractLists(res: any): any[] {
  if (Array.isArray(res)) return res;

  const listsDirect = res && typeof res === "object" ? (res as any).lists : undefined;
  if (Array.isArray(listsDirect)) return listsDirect;

  const listsInEnvelope = getByPath(res, "data.lists");
  if (Array.isArray(listsInEnvelope)) return listsInEnvelope;

  const data = getByPath(res, "data");
  if (Array.isArray(data)) return data;

  return [];
}

function normalizeCharacter(x: any): CharacterVM | null {
  const id = (pickIdByPaths(x, ["id", "characterId"]) ?? "").trim();
  const name = (pickStringByPaths(x, ["name", "characterName"]) ?? "").trim();
  if (!id || !name) return null;

  const description = pickStringByPaths(x, ["description"]) ?? "";

  // iconImageがある場合はAPI_BASE_URLを付加
  const iconImage = pickStringByPaths(x, [
    "iconImage",
    "data.iconImage",
    "character.iconImage",
    "data.character.iconImage",
    "iconUrl",
  ]);
  const iconUrl = resolveBackendAssetUrl(iconImage);

  return {
    id,
    name,
    description,
    initial: name.slice(0, 1).toUpperCase(),
    iconUrl,
  };
}

function getCommunicationStyleValue(profiles: readonly UserProfileItem[]) {
  // 空値・旧値・不明値は「フランク」に正規化する（画面が壊れないように）。
  return resolveCommunicationStyle(profiles.find((item) => item.key === COMMUNICATION_STYLE_KEY)?.value);
}

function buildCommunicationStyleProfilePayload(
  profiles: readonly UserProfileItem[],
  nextCommunicationStyle: string
): UserProfileUpdateItem[] {
  const usedKeys = new Set<string>();
  const preservedProfiles = profiles.flatMap<UserProfileUpdateItem>((item) => {
    if (item.key === COMMUNICATION_STYLE_KEY) return [];
    if (!item.key.trim() || !item.value.trim()) return [];
    if (usedKeys.has(item.key)) return [];
    usedKeys.add(item.key);
    return [{ key: item.key, value: item.value }];
  });

  return [
    ...preservedProfiles,
    {
      key: COMMUNICATION_STYLE_KEY,
      value: nextCommunicationStyle,
    },
  ];
}

export default function CharacterSwitch() {
  const [loading, setLoading] = useState(true);
  const [savingCharacterId, setSavingCharacterId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileItems, setProfileItems] = useState<UserProfileItem[]>([]);
  const [communicationStyle, setCommunicationStyle] = useState("");
  const [initialCommunicationStyle, setInitialCommunicationStyle] = useState("");
  const [showPlayfulCommunicationStyles, setShowPlayfulCommunicationStyles] = useState(false);

  const [characters, setCharacters] = useState<CharacterVM[]>([]);
  const [currentCharacterId, setCurrentCharacterId] = useState<string | null>(null);

  const pushDebug = (_d: Omit<DebugInfo, "time">) => {};

  const busy = !!savingCharacterId;
  const communicationStyleChanged = communicationStyle.trim() !== initialCommunicationStyle.trim();

  // ★追加：現在選択中セクションへのスクロール用
  const currentSectionRef = useRef<HTMLElement | null>(null);
  const scrollToCurrent = () => {
    const el = currentSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);

      pushDebug({
        phase: "mount",
        auth: storage.getAuth(),
        device: getDeviceParams(),
        note: "CharacterSwitch mounted; calling GET /character",
      });

      try {
        const auth = storage.getAuth();
        const storedCharacterId = (auth.currentCharacterId ?? "").trim() || null;

        pushDebug({
          phase: "before_fetch",
          auth,
          device: getDeviceParams(),
          note: `storage currentCharacterId=${storedCharacterId ?? "null"}`,
        });

        const resCharacter = await api.getCharacterList();
        if (!alive) return;

        const rawCharacters = extractLists(resCharacter);

        const charList = rawCharacters.map(normalizeCharacter).filter(Boolean) as CharacterVM[];

        setCharacters(charList);

        const nextCurrentCharacterId =
          storedCharacterId && charList.some((c) => c.id === storedCharacterId) ? storedCharacterId : null;

        setCurrentCharacterId(nextCurrentCharacterId);

        pushDebug({
          phase: "state_ready",
          auth: storage.getAuth(),
          device: getDeviceParams(),
          normalized: {
            extractedCharacters: rawCharacters.length,
            normalizedCharacters: charList.length,
            storedCharacterId,
            matchedCharacter: nextCurrentCharacterId,
          },
          note: "normalized & matched by storage",
        });
      } catch (e) {
        logError(e);
        if (!alive) return;

        const msg = toUserMessage(e);
        setError(msg);

        pushDebug({
          phase: "fetch_error",
          auth: storage.getAuth(),
          device: getDeviceParams(),
          note: `error: ${msg}`,
        });
      } finally {
        if (!alive) return;
        setLoading(false);
        pushDebug({
          phase: "done",
          auth: storage.getAuth(),
          device: getDeviceParams(),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      setProfileLoading(true);
      setProfileError(null);
      setProfileSuccess(null);

      try {
        const userProfiles = await api.getUserProfile();
        if (!alive) return;

        const nextCommunicationStyle = getCommunicationStyleValue(userProfiles);
        setProfileItems(userProfiles);
        setCommunicationStyle(nextCommunicationStyle);
        setInitialCommunicationStyle(nextCommunicationStyle);
        setShowPlayfulCommunicationStyles(isPlayfulCommunicationStyle(nextCommunicationStyle));
      } catch (e) {
        logError(e);
        if (!alive) return;
        setProfileError(toUserMessage(e));
      } finally {
        if (alive) setProfileLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const currentCharacter = useMemo(() => {
    if (!currentCharacterId) return null;
    return characters.find((c) => c.id === currentCharacterId) ?? null;
  }, [characters, currentCharacterId]);

  // 一覧用（現在選択中は除外）
  const listCharacters = useMemo(() => {
    if (!currentCharacterId) return characters;
    return characters.filter((c) => c.id !== currentCharacterId);
  }, [characters, currentCharacterId]);

  const canShowList = !loading && !error && characters.length > 0;
  const canShowCurrent = canShowList && !!currentCharacter;

  const onSelectCharacter = async (c: CharacterVM) => {
    if (busy) return;

    const prev = storage.getAuth();
    const prevCharacterId = (prev.currentCharacterId ?? "").trim() || null;

    const nextCharacterId = c.id;

    setSavingCharacterId(c.id);
    setError(null);

    try {
      storage.setAuth({ currentCharacterId: nextCharacterId });

      await api.updateUserSelection({ characterId: nextCharacterId });

      setCurrentCharacterId(nextCharacterId);

      // ★追加：切替完了後、現在選択中の位置へスクロール
      requestAnimationFrame(() => scrollToCurrent());
    } catch (e) {
      logError(e);
      const msg = toUserMessage(e);
      setError(msg);

      storage.setAuth({ currentCharacterId: prevCharacterId ?? undefined });
      setCurrentCharacterId(prevCharacterId);
    } finally {
      setSavingCharacterId(null);
    }
  };

  const selectCommunicationStyle = (nextStyle: string) => {
    setCommunicationStyle(nextStyle);
    setProfileError(null);
    setProfileSuccess(null);
  };

  const onSaveCommunicationStyle = async () => {
    const nextCommunicationStyle = communicationStyle.trim();
    if (!nextCommunicationStyle) {
      setProfileError("話し方の好みを選んでください。");
      setProfileSuccess(null);
      return;
    }

    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      const savedProfiles = await api.updateUserProfile({
        profiles: buildCommunicationStyleProfilePayload(profileItems, nextCommunicationStyle),
      });
      const savedCommunicationStyle = getCommunicationStyleValue(savedProfiles) || nextCommunicationStyle;
      setProfileItems(savedProfiles);
      setCommunicationStyle(savedCommunicationStyle);
      setInitialCommunicationStyle(savedCommunicationStyle);
      setProfileSuccess("話し方の好みを保存しました。");
    } catch (e) {
      logError(e);
      setProfileError(toProfileSaveErrorMessage(e));
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased">
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 mx-auto w-full max-w-md px-4 py-6 space-y-6">
          <div>
            <p className="text-[11px] font-black tracking-[0.14em] text-[#A67C52]">設定</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-[#7C5A4E]">キャラクター設定</h1>
          </div>
          {/* 現在選択中 */}
          <section
            ref={currentSectionRef as any}
            className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_10px_25px_rgba(166,124,82,0.12)] overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-[#7C5A4E]">現在選択中</p>
                <span className="inline-flex items-center rounded-full bg-[#D1907B]/20 px-2 py-0.5 text-[11px] font-bold text-[#8E6E60]">
                  ACTIVE
                </span>
              </div>

              {loading && <p className="mt-3 text-xs text-[#7B6861]">読み込み中…</p>}
              {!loading && error && <p className="mt-3 text-xs text-[#AF3B2E]">{error}</p>}

              {!loading && !error && !canShowCurrent && (
                <p className="mt-3 text-xs text-[#7B6861]">現在選択中のキャラクターが見つかりません。</p>
              )}

              {canShowCurrent && currentCharacter && (
                <>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="relative">
                      <div className="h-14 w-14 rounded-full border border-[#EADBC8] bg-white shadow-sm overflow-hidden">
                        <img
                          src={currentCharacter.iconUrl || "/img/no-image.png"}
                          alt={currentCharacter.name}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src !== "/img/no-image.png") {
                              target.src = "/img/no-image.png";
                            }
                          }}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <span className="absolute -right-1 -bottom-1 h-4 w-4 rounded-full border-2 border-white bg-[#D1907B]" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#4A3E39]">{currentCharacter.name}</p>
                      <p className="mt-0.5 text-xs text-[#7B6861] truncate">{currentCharacter.description}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* キャラクター一覧（現在選択中は表示しない） */}
          <section className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_10px_25px_rgba(166,124,82,0.12)] overflow-hidden">
            <div className="px-6 py-5 border-b border-[#EADBC8]">
              <h1 className="text-sm font-bold tracking-tight text-[#4A3E39]">キャラクター一覧</h1>
              <p className="mt-1 text-xs text-[#7B6861]">タップして切り替え</p>
            </div>

            <div className="p-2 max-h-[60vh] overflow-y-auto pr-1">
              {canShowList &&
                listCharacters.map((c) => {
                  const savingThis = savingCharacterId === c.id;

                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={busy}
                      onClick={() => onSelectCharacter(c)}
                      className={`w-full text-left rounded-2xl border border-transparent px-4 py-4 transition hover:bg-[#FEFBF9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1907B]/20 active:scale-[0.99] ${
                        busy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <div className="h-11 w-11 rounded-full border border-[#EADBC8] bg-white shadow-sm overflow-hidden">
                            <img
                              src={c.iconUrl || "/img/no-image.png"}
                              alt={c.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target.src !== "/img/no-image.png") {
                                  target.src = "/img/no-image.png";
                                }
                              }}
                              className="h-full w-full object-cover object-top"
                            />
                          </div>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-bold text-[#4A3E39] truncate">
                              {c.name}
                              {savingThis && <span className="ml-2 text-xs text-[#7B6861]">保存中…</span>}
                            </p>
                            <span className="shrink-0 rounded-full border border-[#EADBC8] bg-white px-2 py-0.5 text-[11px] font-bold text-[#7C5A4E]">
                              選択
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-[#7B6861] truncate">{c.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}

              {!loading && !error && canShowList && listCharacters.length === 0 && (
                <p className="px-4 py-6 text-xs text-[#7B6861]">切り替え可能なキャラクターがありません。</p>
              )}

              {!loading && !error && !canShowList && (
                <p className="px-4 py-6 text-xs text-[#7B6861]">キャラクターが取得できませんでした。</p>
              )}
            </div>

          </section>

          <section className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_10px_25px_rgba(166,124,82,0.12)] overflow-hidden">
            <div className="px-6 py-5 border-b border-[#EADBC8]">
              <h1 className="text-sm font-bold tracking-tight text-[#4A3E39]">話し方の好み</h1>
              <p className="mt-1 text-xs leading-5 text-[#7B6861]">
                キャラクターの話し方は、あとでいつでも変更できます。
              </p>
            </div>

            <div className="p-5">
              {profileLoading ? <p className="text-xs text-[#7B6861]">読み込み中…</p> : null}

              {!profileLoading ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    {COMMUNICATION_STYLE_OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => selectCommunicationStyle(option)}
                        aria-pressed={communicationStyle.trim() === option}
                        className={`min-h-[2.5rem] rounded-full border px-3 py-2 text-sm font-bold transition ${
                          communicationStyle.trim() === option
                            ? "border-[#7C5A4E] bg-[#7C5A4E] text-white shadow-[0_8px_18px_rgba(124,90,78,0.18)]"
                            : "border-[#EADBC8] bg-white text-[#5D4D46] hover:bg-[#FFF8F4]"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowPlayfulCommunicationStyles((prev) => !prev)}
                    className="mt-4 rounded-full border border-[#EADBC8] bg-[#FFF8F4] px-3 py-2 text-xs font-black text-[#7C5A4E] transition hover:bg-white"
                  >
                    {showPlayfulCommunicationStyles ? "遊び心のある話し方を閉じる" : "もっと遊び心のある話し方"}
                  </button>

                  {showPlayfulCommunicationStyles ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {PLAYFUL_COMMUNICATION_STYLE_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => selectCommunicationStyle(option)}
                          aria-pressed={communicationStyle.trim() === option}
                          className={`min-h-[2.5rem] rounded-full border px-3 py-2 text-sm font-bold transition ${
                            communicationStyle.trim() === option
                              ? "border-[#7C5A4E] bg-[#7C5A4E] text-white shadow-[0_8px_18px_rgba(124,90,78,0.18)]"
                              : "border-[#EADBC8] bg-white text-[#5D4D46] hover:bg-[#FFF8F4]"
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {profileError ? (
                    <p role="alert" className="mt-4 rounded-[1rem] bg-[#FFF3F1] px-4 py-3 text-xs font-bold text-[#A1493E]">
                      {profileError}
                    </p>
                  ) : null}

                  {profileSuccess ? (
                    <p role="status" className="mt-4 rounded-[1rem] bg-[#F3F8EE] px-4 py-3 text-xs font-bold text-[#5D7A4A]">
                      {profileSuccess}
                    </p>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => void onSaveCommunicationStyle()}
                    disabled={profileSaving || !communicationStyle.trim() || !communicationStyleChanged}
                    className="mt-5 min-h-[3rem] w-full rounded-[1.3rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {profileSaving ? "保存中…" : "話し方を保存"}
                  </button>
                </>
              ) : null}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
